const getScrollVal = () => window.scrollY || document.documentElement.scrollTop;

class CollapsibleHeader extends Collapsible {
  constructor() {
    super();
    this.header = this.closest('[data-header]');
    this.headerWrapper = this.closest('.shopify-section-group-header-group');
    this.headerBackdrop = this.headerWrapper.querySelector('[data-header-backdrop]');

    if (this.header) {
      this.inverse = !!this.header.classList.contains('header--inverse');
      this.shadow = this.header.querySelector('.header-shadow');
      // extra events
      this.shadow?.addEventListener('click', this.closeAll.bind(this));
      this.headerBackdrop?.addEventListener('click', this.closeAll.bind(this));
      // on key up
      document.addEventListener('keyup', (event) => {
        this.onKeyUp(event);
      });
    }
  }

  events() {
    if (!this.header) {
      super.events();
    }
  }

  onKeyUp(event) {
    if (!document.body.classList.contains('header-is-open')) {
      return;
    }
    if (event?.code.toUpperCase() !== 'ESCAPE') {
      return;
    }
    const { toggleClass } = this.options;
    let group = this.querySelector(`[data-collapsible-group].${toggleClass}`);
    if (group) {
      this.close(group);
    }
  }

  open(group) {
    super.open(group);
    if (this.headerWrapper) {
      document.body.classList.add('header-is-open');
    }
    this.toggleHeaderClass(true);
  }

  close(group) {
    super.close(group);
    const level2 = group.querySelector('.level-2');
    if (this.headerWrapper && !level2) {
      document.body.classList.remove('header-is-open');
    }
    this.toggleHeaderClass(false);
  }

  closeSiblings(currentGroup = null) {
    super.closeSiblings(currentGroup);
    this.toggleHeaderClass(false);
  }

  openAll() {
    super.openAll();
    this.toggleHeaderClass(true);
    if (this.headerWrapper) {
      document.body.classList.add('header-is-open');
    }
  }

  closeAll() {
    super.closeAll();
    this.toggleHeaderClass(false);
    if (this.headerWrapper) {
      document.body.classList.remove('header-is-open');
    }
  }

  toggleHeaderClass(open = true) {
    if (!this.header) {
      return false;
    }

    if (open) {
      // set header open
      document.body.classList.add('header-is-open');
      this.inverse && this.header.classList.add('header-inverse-solid');
    } else {
      // set header closed if all menu items are closed
      let close = true;
      this.groups.forEach((group) => {
        if (group.classList.contains(this.options.toggleClass)) {
          close = false;
        }
      });
      if (close) {
        document.body.classList.remove('header-is-open');
        this.inverse && document.body.classList.remove('header-inverse-solid');
      }
    }
  }
}

class ToggleMobileNavigation extends ToggleElementClass {
  constructor() {
    super();

    this.header = this.closest('[data-header]');

    if (this.header) {
      this.shadow = this.header.querySelector('.header-shadow');
      this.inverse = !!this.header.classList.contains('header--inverse');

      // Extra events
      document.addEventListener('keyup', (event) => {
        this.onKeyUp(event);
      });

      this.shadow?.addEventListener('click', (event) => {
        this.remove(event);
      });
    }
  }

  events() {
    if (!this.header) {
      super.events();
    }
  }

  onKeyUp(event) {
    if (!document.body.classList.contains('menu-drawer-is-open')) {
      return;
    }
    if (event?.code.toUpperCase() !== 'ESCAPE') {
      return;
    }
    const { toggleClass } = this.options;
    if (this.target?.classList.contains(toggleClass)) {
      this.remove();
    }
  }

  add() {
    super.add();
    trapFocus(this.header);
  }

  remove() {
    super.remove();
    this.closeAllSiblings();
    removeTrapFocus();
  }

  closeAllSiblings() {
    const collapsibles = this.target.querySelector('collapsible-element');
    collapsibles.closeAll();
  }
}

class ToggleSearchDrawer extends ToggleElementClass {
  constructor() {
    super();

    this.header = this.closest('[data-header]');

    if (this.header) {
      this.shadow = this.header.querySelector('.header-shadow');
      this.inverse = !!this.header.classList.contains('header--inverse');

      // Extra events
      document.addEventListener('keyup', (event) => {
        this.onKeyUp(event);
      });

      this.shadow?.addEventListener('click', (event) => {
        this.remove(event);
      });
    }
  }

  events() {
    if (!this.header) {
      super.events();
    }
  }

  onKeyUp(event) {
    if (!document.body.classList.contains('search-drawer-is-open')) {
      return;
    }
    if (event?.code.toUpperCase() !== 'ESCAPE') {
      return;
    }
    const { toggleClass } = this.options;
    if (this.target?.classList.contains(toggleClass)) {
      this.remove();
    }
  }

  add() {
    super.add();
    trapFocus(this.header);
  }

  remove() {
    super.remove();
    this.closeAllSiblings();
    removeTrapFocus();
  }

  closeAllSiblings() {
    const collapsibles = this.target.querySelector('collapsible-element');
    collapsibles.closeAll();
  }
}

class ToggleSearch extends ToggleElementClass {
  constructor() {
    super();
  }

  construct() {
    super.construct();

    // elements
    this.header = this.closest('[data-header]');

    // options
    const { toggleClass, target } = this.options;

    // key up escape
    document.addEventListener('keyup', (event) => {
      this.onKeyUp(event);
    });

    // Remove classes in case the header navigation drawer is open.
    this.addEventListener('click', () => {
      document.body.classList.remove('header-is-open');
      document.body.classList.remove('menu-drawer-is-open');
      // Focus on input element if provided
      if (this.options.focusTarget) {
        document.querySelector(this.options.focusTarget)?.focus();
      }
    });
  }

  onKeyUp(event) {
    if (!document.body.classList.contains('header-search-is-open')) {
      return;
    }
    if (event?.code.toUpperCase() !== 'ESCAPE') {
      return;
    }
    const { toggleClass } = this.options;
    if (this.target?.classList.contains(toggleClass)) {
      this.remove();
    }
  }

  add() {
    super.add();
    if (this.header) {
      document.body.classList.add('header-search-is-open');
    }
    if (this.target) {
      trapFocus(this.target);
    }
  }

  remove() {
    super.remove();
    if (this.header) {
      document.body.classList.remove('header-search-is-open');
    }
    removeTrapFocus();
  }
}

class StickyHeader extends HTMLElement {
  constructor() {
    super();

    // select element
    this.headerElement = this;
    this.header = document.querySelector('[data-header]');
    this.headerWrapper = document.querySelector('.shopify-section-group-header-group');
    this.headerInverse = !!this.headerElement.classList.contains('header--inverse');
    this.headerNav = this.querySelector('collapsible-header');
    this.mobileNav = this.querySelector('toggle-mobile-navigation');
    this.searchDrawer = this.querySelector('toggle-search-drawer');
    this.search = this.querySelector('[data-header-search]');
    this.searchToggle = this.querySelector('toggle-search');
    this.productInfo = document.querySelector('[data-product-info]');
    /** @type {'full' | 'minimized' | 'hidden'} */
    this.headerState = 'full';

    this.announcementBar = document.querySelector('.section-announcement sticky-announcement');

    if (this.headerWrapper) {
      // Dynamically set margin-bottom on .section-header based on actual header height
      this.sectionHeader = this.closest('.section-header');
      if (this.sectionHeader) {
        this.updateHeaderMargin();
        // Observe the sticky-header element itself (this.headerElement), not headerWrapper:
        // headerWrapper's own box height collapses because sticky-header is position:absolute,
        // so it doesn't reflect the real rendered height (e.g. the alternate-search mobile row).
        new ResizeObserver(() => this.updateHeaderMargin()).observe(this.headerElement);
      }

      // scroll parameters
      this.currentScrollTop = getScrollVal();
      this.scrollOffset = 0;
      this.headerStart = 0;

      if (this.announcementBar) {
        const announcementBarHeight = this.announcementBar.offsetHeight;
        this.headerStart += announcementBarHeight;
      }

      if (this.headerElement) {
        this.scrollOffset += this.headerElement.offsetHeight;
        this.headerStart += this.headerElement.offsetHeight;
      }

      // scroll event
      this.raf = null; // Safe request animation frame
      (this.timer = null), // Safe scroll timer
        window.addEventListener(
          'scroll',
          () => {
            clearTimeout(this.timer);
            if (!this.raf) {
              // A request animation frame is used to animate on max 60fps.
              this.raf = requestAnimationFrame(this.scrollAnimation.bind(this));
            }
            this.timer = setTimeout(() => {
              // Stop animation, cancel request animation frame
              cancelAnimationFrame(this.raf);
              this.raf = null;
            }, 50);
          },
          false
        );
    }
  }

  /**
   * @returns @type {{
   * full: string,
   * minified: string,
   * hidden: string,
   * fullClass: string,
   * minifiedClass: string,
   * hiddenClass: string
   * }}
   */
  get headerStates() {
    const FULL = 'header-full';
    const MINIFIED = 'header-minified';
    const HIDDEN = 'header-hidden';

    return {
      full: FULL,
      minified: MINIFIED,
      hidden: HIDDEN,
      fullClass: `.${FULL}`,
      minifiedClass: `.${MINIFIED}`,
      hiddenClass: `.${HIDDEN}`,
    };
  }

  getScrollDirection() {
    const scrollVal = getScrollVal();

    if (this.currentScrollTop > scrollVal) return 'up';

    if (this.currentScrollTop < scrollVal) return 'down';

    return null;
  }

  shouldHideHeader(scrollTop) {
    const scrollDirection = this.getScrollDirection();

    if (!scrollDirection) return;

    if (this.headerState === 'hidden') return;
    if (document.querySelector('body').classList.contains('menu-drawer-is-open')) return;
    return scrollDirection === 'down' && scrollTop > this.scrollOffset;
  }

  shouldShowMinimizedHeader(scrollTop) {
    const scrollDirection = this.getScrollDirection();

    if (!scrollDirection) return;

    if (this.headerState === 'full' || this.headerState === 'minimized') return;

    return scrollDirection === 'up' && scrollTop > this.scrollOffset;
  }

  shouldShowFullHeader(scrollTop) {
    const scrollDirection = this.getScrollDirection();

    if (this.headerState === 'full') return;

    return scrollTop <= this.headerStart && scrollDirection === 'up';
  }

  scrollAnimation() {
    const scrollTop = getScrollVal();

    // only animate if header menu is closed.
    if (!document.body.classList.contains('header-is-open')) {
      if (this.shouldHideHeader(scrollTop)) {
        this.showHiddenState();
        this.resetToTop();
      } else if (this.shouldShowMinimizedHeader(scrollTop)) {
        this.showMinimizedState();
        this.calculateTopOffset();
      } else if (this.shouldShowFullHeader(scrollTop)) {
        this.showFullState();
      }
    }

    this.currentScrollTop = scrollTop;

    // Recall request animation loop
    this.raf = requestAnimationFrame(this.scrollAnimation.bind(this));
  }

  showHiddenState() {
    this.headerState = 'hidden';
    document.body.classList.remove(this.headerStates.minified, this.headerStates.full);
    document.body.classList.add(this.headerStates.hidden);
    this.closeHeaderNav();
    this.closeMobileNav();
    this.closeSearch();
    this.productInfoPosition('up');
  }

  showFullState() {
    this.headerState = 'full';
    document.body.classList.add(this.headerStates.full);
    document.body.classList.remove(this.headerStates.hidden, this.headerStates.minified);

    if (this.headerInverse) {
      document.body.classList.add('header-inverse-solid');
    }

    this.productInfoPosition('down');
  }

  showMinimizedState() {
    this.headerState = 'minimized';

    if (this.headerInverse && !document.body.classList.contains('header-is-open')) {
      document.body.classList.remove('header-inverse-solid');
    }

    document.body.classList.add(this.headerStates.minified);
    document.body.classList.remove(
      this.headerStates.hidden,
      'header-search-show',
      this.headerStates.full
    );
    this.productInfoPosition('up');
  }

  updateHeaderMargin() {
    if (this.sectionHeader && this.headerElement) {
      this.sectionHeader.style.marginBottom = this.headerElement.offsetHeight + 'px';
    }
  }

  calculateTopOffset() {
    if (this.closest('.section-header')) {
      this.closest('.section-header').style.top = this.announcementBar?.clientHeight + 'px' || 0;
    }
  }

  resetToTop() {
    if (this.closest('.section-header')) {
      this.closest('.section-header').style.top = 0;
    }
  }

  closeHeaderNav() {
    if (!this.headerNav || window.innerWidth < 768) {
      return false;
    }
    this.headerNav.closeAll();
  }

  closeMobileNav() {
    if (!this.mobileNav) {
      return false;
    }
    this.mobileNav.remove();
  }

  closeSearch() {
    if (!this.search || !this.searchToggle) {
      return false;
    }
    if (this.search.classList.contains('header__search--is-active')) {
      this.searchToggle.remove();
    }
  }

  productInfoPosition(pos = 'up') {
    if (!this.productInfo) {
      return;
    }
    if (pos == 'down') {
      this.productInfo.classList.replace('top-0', 'top-[4.5rem]');
    } else {
      this.productInfo.classList.replace('top-[4.5rem]', 'top-0');
    }
  }
}

if (!customElements.get('collapsible-header')) {
  window.CollapsibleHeader = CollapsibleHeader;
  customElements.define('collapsible-header', CollapsibleHeader);
}

if (!customElements.get('toggle-mobile-navigation')) {
  window.ToggleMobileNavigation = ToggleMobileNavigation;
  customElements.define('toggle-mobile-navigation', ToggleMobileNavigation);
}

if (!customElements.get('toggle-search-drawer')) {
  window.ToggleSearchDrawer = ToggleSearchDrawer;
  customElements.define('toggle-search-drawer', ToggleSearchDrawer);
}

if (!customElements.get('toggle-search')) {
  window.ToggleSearch = ToggleSearch;
  customElements.define('toggle-search', ToggleSearch);
}

if (!customElements.get('sticky-header')) {
  window.StickyHeader = StickyHeader;
  customElements.define('sticky-header', StickyHeader);
}
